import { Avatar } from "antd";
import { useCallback, useState } from "react";

import { cn } from "@btnet/utilities-core";

import { getInitials, isNullOrWhitespace } from "utilities/string/string";

import { type BTAvatarDeprecatedSize } from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated.types";
import { sizeNameToPixels } from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated.utils";
import { BTIconUser } from "commonComponents/btWrappers/BTIcon";
import { Thumbnail } from "commonComponents/utilities/Thumbnail/Thumbnail";

import "./BTAvatarDeprecated.less";

interface IBTAvatarDeprecatedProps {
    /**
     * @default middle
     */
    size?: BTAvatarDeprecatedSize;

    /** Src of image avatar */
    src?: string | null;

    /** User display name (first and last name), avoid using username if possible
     * @example "John Doe"
     */
    displayName: string;

    className?: string;

    style?: React.CSSProperties;

    icon?: React.ReactNode;
    useInitials?: boolean;
}

const defaultIcon = <BTIconUser />;

/**
 * @deprecated Use BTUser instead
 * @see BTUser
 */
export const BTAvatarDeprecated: React.FunctionComponent<IBTAvatarDeprecatedProps> = ({
    size = "middle",
    src,
    displayName,
    className,
    style,
    icon = defaultIcon,
    useInitials = true,
}) => {
    const sizeInPixels = sizeNameToPixels(size);

    // if the avatar src fails to load use displayName instead
    const [error, setError] = useState<boolean>(false);
    const srcEmpty = isNullOrWhitespace(src);

    const handleError = useCallback(() => {
        setError(true);
    }, []);

    const iconDisplayName = useInitials ? getInitials(displayName) : displayName;

    return (
        // eslint-disable-next-line react/forbid-elements
        <Avatar
            size={sizeInPixels}
            gap={sizeInPixels / 4}
            src={
                error || srcEmpty ? undefined : (
                    <Thumbnail
                        src={src}
                        width={sizeInPixels}
                        height={sizeInPixels}
                        alt={displayName}
                        onError={handleError}
                    />
                )
            }
            className={cn("BTAvatarDeprecated", `BTAvatarDeprecatedSize-${size}`, className)}
            style={style}
            icon={iconDisplayName.length === 0 ? icon : undefined}
        >
            {iconDisplayName}
        </Avatar>
    );
};
